import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { NAV_LINKS } from '../mock';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0b0b0d]/85 backdrop-blur-md border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 flex items-center justify-between gap-6 h-[76px]">
        <Link to="/" className="flex items-center select-none shrink-0">
          <img
            src="/assets/yellow-logo.png"
            alt="Yellow, Marketing Agency Liverpool"
            className="h-10 md:h-11 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-4 xl:gap-6 2xl:gap-8">
          {NAV_LINKS.map((link) => {
            if (link.children) {
              return (
                <div key={link.label} className="relative group">
                  <button
                    className="flex items-center gap-1 text-sm font-medium text-white/90 hover:text-[#F5CD1B] transition-colors py-2 whitespace-nowrap"
                  >
                    {link.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200">
                    <div className="bg-[#111114] border border-white/10 rounded-xl shadow-2xl p-2 min-w-[240px]">
                      {link.children.map((c) => (
                        <Link
                          key={c.path}
                          to={c.path}
                          className="block px-4 py-2.5 text-sm text-white/80 hover:text-[#F5CD1B] hover:bg-white/[0.04] rounded-lg transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium whitespace-nowrap reveal-underline ${
                  active ? 'text-[#F5CD1B] active' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 btn-yellow px-5 py-3 rounded-md text-sm uppercase"
          >
            Contact Us <ArrowRight size={16} />
          </Link>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0b0b0d] border-t border-white/5 max-h-[85vh] overflow-y-auto">
          <div className="px-6 py-6 space-y-1">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label} className="py-2">
                  <button
                    className="flex items-center justify-between w-full text-white font-semibold py-2"
                    onClick={() => setServicesOpen(!servicesOpen)}
                  >
                    {link.label}
                    <ChevronDown size={16} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen && (
                    <div className="pl-3 border-l border-white/10 mt-2 space-y-1">
                      {link.children.map((c) => (
                        <Link key={c.path} to={c.path} className="block py-2 text-sm text-white/70 hover:text-[#F5CD1B]">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block py-2 text-white/90 hover:text-[#F5CD1B] font-medium"
                >
                  {link.label}
                </Link>
              )
            )}
            <Link to="/contact" className="btn-yellow inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm uppercase mt-4">
              Contact Us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
